import { useEffect, useMemo } from 'react'
import currency from 'currency.js'

import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { centsToCurrency, formatCurrency } from '@/utils/currencyHelpers'
import { fetchCatchUpBKCoupon } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { getProductPermissions } from '@/actions/permissionActions'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { fetchFreeTrialLength } from '../helpers'
import {
  catchUpBkTreatment,
  discountedCatchUpBkAmount,
  planSubText,
} from './helpers'
import {
  PLAN_UNAVAILABLE_MESSAGE,
  cadencePriceView,
  cadenceProductIdFor,
} from './catalogHelpers'
import { useAnnualCommitmentPrice, useCatchupBookkeepingPrice } from './hooks'
import { CatchupBookkeepingSection } from './CatchupBookkeepingSection'
import { CatalogPlanDetailsSection } from './CatalogPlanDetailsSection'
import { TextColors, TypographyStyles } from '@/styles'
import { Card, Separator, Text, Toggle } from '@/ui'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  SIGNUP_CADENCES,
  type SignupCadence,
  type SignupCard,
} from '../plans/signupPlans.types'
import { cadencesOf } from '../plans/signupPlans'

export const CatalogStripeProductCard = ({
  plan,
  cadence,
  onCadenceChange,
  freeTrialEnabled,
  freeTrialSearchParamValue,
  useTwoCheckoutFlow,
}: {
  plan: SignupCard
  cadence: SignupCadence
  onCadenceChange: (cadence: SignupCadence) => void
  freeTrialEnabled: boolean
  freeTrialSearchParamValue: string | null
  useTwoCheckoutFlow: boolean
}) => {
  const cubkWaiverEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.waiveCubkForFreeTrial2026,
    false
  )
  const catchUpBk = catchUpBkTreatment({
    trialCode: freeTrialSearchParamValue,
    freeTrialEnabled,
    useTwoCheckoutFlow,
    waiverEnabled: cubkWaiverEnabled,
  })
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const isAnnual = cadence === SIGNUP_CADENCES.year
  const offersBothCadences = cadencesOf(plan).length > 1
  const selectedCadencePrice = plan.prices[cadence]
  // The plan claims to price this cadence, yet it carries no amount: a catalog price
  // with nothing to charge, not a cadence the plan simply doesn't offer.
  const priceUnavailable =
    Boolean(selectedCadencePrice) &&
    typeof selectedCadencePrice?.unitAmountInCents !== 'number'
  const annualCadencePrice = cadencePriceView(plan, SIGNUP_CADENCES.year)
  const monthlyCadencePrice = cadencePriceView(plan, SIGNUP_CADENCES.month)
  const selectedPrice = cadencePriceView(plan, cadence)
  const planTier = plan.tier
  const catchUpBKCoupon = useFetchResponse(fetchCatchUpBKCoupon, isAnnual)
  const cadenceProductId = cadenceProductIdFor(plan, cadence)
  const productPermissions = useFetchResponse(getProductPermissions, {
    productIds: [cadenceProductId],
  })
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const { priceAsCurrency } = useAnnualCommitmentPrice()
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const track = useAnalyticsTrack()

  useEffect(() => {
    track(
      `viewed ${planTier}, ${isAnnual ? 'Annual Plan' : 'Monthly Plan'} option in choose-plan`
    )
  }, [track, isAnnual, planTier])

  const productPrice = priceAsCurrency(selectedPrice)

  const totalDueToday = useMemo(() => {
    if (catchupBookkeepingPrice) {
      return productPrice.add(
        discountedCatchUpBkAmount(
          catchupBookkeepingPrice.amount,
          catchUpBKCoupon?.percent_off
        )
      )
    }

    return productPrice
  }, [catchupBookkeepingPrice, productPrice, catchUpBKCoupon])

  const subText = useMemo(() => {
    const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)

    if (freeTrialEnabled) {
      const discountedCubk = catchupBookkeepingPrice
        ? discountedCatchUpBkAmount(
            catchupBookkeepingPrice.amount,
            catchUpBKCoupon?.percent_off
          )
        : null
      return planSubText({
        treatment: catchUpBk,
        freeTrialDays,
        productPriceLabel: formatCurrency(productPrice, {
          hideDecimalsIfZero: true,
        }),
        isAnnual,
        catchUpBkLabel:
          discountedCubk && discountedCubk.value > 0
            ? formatCurrency(discountedCubk, { hideDecimalsIfZero: true })
            : null,
      })
    } else if (catchupBookkeepingPrice) {
      return `Then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }`
    }

    return `Billed ${isAnnual ? 'annually' : 'monthly'}`
  }, [
    freeTrialEnabled,
    freeTrialSearchParamValue,
    catchupBookkeepingPrice,
    catchUpBKCoupon,
    isAnnual,
    productPrice,
    catchUpBk,
  ])

  const handleCadenceToggle = () => {
    onCadenceChange(isAnnual ? SIGNUP_CADENCES.month : SIGNUP_CADENCES.year)
  }

  const monthlySavingsOnAnnualPlan = useMemo(() => {
    if (!isAnnual || !annualCadencePrice || !monthlyCadencePrice) return null
    const annualMonthlyPrice = centsToCurrency(
      annualCadencePrice.unitAmountInCents
    ).divide(12)
    const monthlyPrice = priceAsCurrency(monthlyCadencePrice)
    const monthlySavings = monthlyPrice.subtract(annualMonthlyPrice)
    return formatCurrency(monthlySavings, { hideDecimalsIfZero: true })
  }, [isAnnual, annualCadencePrice, monthlyCadencePrice, priceAsCurrency])

  const showFreeTrialAnnualVersion =
    freeTrialEnabled && isAnnual && !hasAnnualCommitment

  return (
    <div className="flex flex-col gap-8">
      {offersBothCadences && (
        <div className="flex flex-row gap-2">
          <Toggle
            size="lg"
            variant="outline"
            checked={isAnnual}
            onCheckedChange={() => handleCadenceToggle()}
          >
            <Text className="font-medium">
              {hasAnnualCommitment ? 'Annual Payment' : 'Annual Plan'}
            </Text>
            {!hasAnnualCommitment && (
              <Text className="text-green-50">Best Value</Text>
            )}
          </Toggle>
          <Toggle
            size="lg"
            variant="outline"
            checked={!isAnnual}
            onCheckedChange={() => handleCadenceToggle()}
          >
            <Text className="font-medium">
              {hasAnnualCommitment ? 'Monthly Payment' : 'Monthly Plan'}
            </Text>
          </Toggle>
        </div>
      )}

      {priceUnavailable ? (
        <Card className={`!mt-0 ${isMobile ? 'px-4' : ''}`}>
          <Text variant={TypographyStyles.H2}>Plan unavailable</Text>
          <Text>{PLAN_UNAVAILABLE_MESSAGE}</Text>
        </Card>
      ) : (
        <Card className={`!mt-0 ${isMobile ? 'px-4' : ''}`}>
          <CatalogPlanDetailsSection
            plan={plan}
            cadence={cadence}
            freeTrialEnabled={freeTrialEnabled}
            freeTrialSearchParamValue={freeTrialSearchParamValue}
            monthlySavingsOnAnnualPlan={monthlySavingsOnAnnualPlan}
            productPermissions={productPermissions}
          />

          <CatchupBookkeepingSection
            selectedAnnualPlan={isAnnual}
            planTier={planTier}
            freeTrialEnabled={freeTrialEnabled}
            catchUpBkTreatment={catchUpBk}
            selectedProductId={cadenceProductId}
            productPermissions={productPermissions}
          />

          <Separator className="mt-10 mb-4" />

          <div className="flex flex-col gap-4">
            <div className="flex items-center">
              <Text className="w-1/2" variant={TypographyStyles.LABEL_LG}>
                Total Due Today
              </Text>

              <div className="w-1/2 text-right">
                {showFreeTrialAnnualVersion && (
                  <Text
                    variant={TypographyStyles.DISPLAY_XXS}
                    className="inline line-through mr-2"
                    color={TextColors.MUTED}
                  >
                    {formatCurrency(totalDueToday, {
                      hideDecimalsIfZero: true,
                    })}
                  </Text>
                )}

                <Text
                  variant={
                    isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_MD
                  }
                  className="inline"
                >
                  {formatCurrency(
                    freeTrialEnabled ? currency(0) : totalDueToday,
                    {
                      hideDecimalsIfZero: true,
                    }
                  )}
                </Text>

                {!catchupBookkeepingPrice && (
                  <Text
                    variant={
                      isMobile ? TypographyStyles.H2 : TypographyStyles.H1
                    }
                    className="inline"
                  >
                    /{isAnnual ? 'yr' : 'mo'}
                  </Text>
                )}
              </div>
            </div>

            <Text
              className="min-h-fit text-right"
              color={TextColors.BRAND}
              variant={TypographyStyles.BODY_MD}
            >
              {subText}
            </Text>
          </div>
        </Card>
      )}
    </div>
  )
}
